/* eslint-disable max-len */
import React from 'react';
import ButtonWithTooltip from '../../../Components/ButtonWithTooltip';
import isPastDate from '../../../Utils/isPastDate';
import { isSystemManagedStatus, systemManagedStatusLabel } from '../../../Utils/bookingStatus';
import { isExpenseBooking } from '../../../Utils/bookingType';

function hasOverdueReminder(order) {
  if (!order.notifications || order.notifications.length === 0) return false;
  const now = new Date();
  return order.notifications.some((n) => !n.is_completed && new Date(n.due_date) <= now);
}

function OrderWarnings({
  order, assignOperator, markAsCompleted, assignDriver, assignPickupGuide, openReminders,
}) {
  // System-managed statuses (Pending Ingestion, Ingestion Failed): the
  // ingestion pipeline owns the row, so "missing operator" / "missing driver"
  // aren't operator-actionable issues — show a single pipeline-status badge
  // instead of CTAs that would invite incorrect dispatch.
  if (isSystemManagedStatus(order.status)) {
    return (
      <ButtonWithTooltip
        variant={order.status === 'Ingestion Failed' ? 'danger' : 'secondary'}
        faClassName={order.status === 'Ingestion Failed' ? 'fa-triangle-exclamation' : 'fa-hourglass-half'}
        message={systemManagedStatusLabel(order.status)}
      />
    );
  }

  /**
   * Every warning below is about a JOURNEY going wrong, and on a cost record
   * most of them would be permanently lit: an expense is retroactive, so it
   * is always past its date; it has no driver, so the driver warning never
   * clears; and its cost lives in `operators[]`, so the flat `operator_id`
   * check would show "Operator is missing" on a fully-recorded expense.
   *
   * The one warning worth keeping is the operator one — an expense with
   * nobody to pay really is incomplete — but it has to ask the question the
   * right way round for this type.
   */
  const isExpense = isExpenseBooking(order);
  const expenseHasNobodyToPay = isExpense
    && !order.operator_id
    && !(order.operators || []).some((op) => op.operator_id);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <div className="d-flex gap-1">
      {(isExpense ? expenseHasNobodyToPay : !order.operator_id) && (
        <ButtonWithTooltip
          variant="danger"
          faClassName="fa-gear"
          message={isExpense ? 'Nobody to pay is recorded' : 'Operator is missing'}
          onClick={() => assignOperator(order)}
        />
      )}

      {/* Hide driver warning for experiences - they use multi-operator
          assignment - and for expenses, which never get a driver at all. */}
      {order.booking_type !== 'experience' && !isExpense && (!order.operator_driver_id && !order.operator_car_id) && order.operator_id && (
        <ButtonWithTooltip
          variant="warning"
          faClassName="fa-car"
          message="Driver or car is missing"
          onClick={() => assignDriver(order)}
        />
      )}

      {/* {((order.payment?.type === 'Bank Transfer' || order.payment?.type === 'Mixed Payment') && !order.payment?.paid) && (
        <ButtonWithTooltip
          variant="warning"
          faClassName="fa-piggy-bank"
          message="Bank Transfer / Mixed Payment - mark as paid"
        />
      )} */}

      {!isExpense && (order.partner.driver_portal_enabled === 1 && !order.contentful_pickup_guide_id) && (
        <ButtonWithTooltip
          variant="danger"
          faClassName="fa-message-sms"
          message="Pickup Guide is not attached"
          onClick={() => assignPickupGuide(order)}
        />
      )}

      {
  (
    (
      isPastDate(order.date_datetime, 2)
      && !order.return_date_datetime
    )
    || (
      isPastDate(order.date_datetime, 2)
      && order.return_date_datetime
      && isPastDate(order.return_date_datetime, 2)
    )
  ) && order.status !== 'Complete' && !isExpense && (
    <ButtonWithTooltip
      variant="info"
      faClassName="fa-check"
      onClick={() => markAsCompleted(order)}
      message={`Mark Completed${order.email_address ? ' & Send Review Email' : ''}`}
    />
  )
}

      {hasOverdueReminder(order) && (
        <ButtonWithTooltip
          variant="warning"
          faClassName="fa-bell"
          message="Reminder overdue"
          onClick={() => openReminders && openReminders(order)}
        />
      )}
    </div>
  );
}

export default OrderWarnings;
